import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { notificationController } from 'infrastructure/antd/controllers/notification-controller';
import { Button } from 'antd';
import { BroadcastTopics } from 'services/wsService';
import { useSocketOn } from 'infrastructure/hooks/websockets/use-socket-on';

import type { IUpdateReadingAction } from 'infrastructure/interfaces/i-rpm-patient-activity';

export const useReadingReviewed = ({
  patientGuid,
  hasUnsavedMessage,
  setPage,
  refetch,
  page,
  onReadingsRefetch,
}: {
  patientGuid: string;
  hasUnsavedMessage: boolean;
  setPage: (page: number) => void;
  refetch: () => void;
  page: number;
  onReadingsRefetch?: () => void;
}) => {
  const { t } = useTranslation();

  const handleReadingReviewed = useCallback(
    (data: IUpdateReadingAction) => {
      if (data.reviewed !== true) {
        return;
      }

      const isPatientGuidMatch = data.patientGuid === patientGuid;
      if (!isPatientGuidMatch) return;

      if (hasUnsavedMessage) {
        return;
      }

      if (page !== 1) {
        const alertKey = 'newReadingGoToFirstPage';
        notificationController.info({
          key: alertKey,
          message: t('messages.newReadingGoToFirstPage'),
          duration: 6,
          actions: (
            <Button
              onClick={() => {
                setPage(1);
                notificationController.destroy(alertKey);
                onReadingsRefetch?.();
              }}
            >
              {t('controls.getLatestReadings')}
            </Button>
          ),
        });
        return;
      }

      refetch();
      onReadingsRefetch?.();
    },
    [patientGuid, page, hasUnsavedMessage, refetch, onReadingsRefetch],
  );

  useSocketOn<IUpdateReadingAction>(
    BroadcastTopics.NewReadingAction,
    handleReadingReviewed,
  );
};
